// The energy of a roll on its way from the die to the pips. The flow gives
// the energy at the click. The stage starts a delivery when the die lands,
// and each coin gives one energy to the views when it drops. `rollShown.ts`
// reads how much is still in the air. The game state never waits for this.

import { REDUCED_MOTION_QUERY } from '../constants';
import type { RollRecord } from '../game/state';
import { createStore } from '../lib/store';
import { type EnergyFlight, planEnergyDelivery } from '../motion/energyFlight';

export type Delivery = {
  /** The roll whose energy is on its way. Null before the first roll of a match. */
  roll: RollRecord | null;
  flights: readonly EnergyFlight[];
  /** The coins that left the die. */
  left: number;
  /** The coins that dropped into a pip. */
  landed: number;
};

const NO_DELIVERY: Delivery = { roll: null, flights: [], left: 0, landed: 0 };

export const deliveryStore = createStore<Delivery>(NO_DELIVERY);

let timers: ReturnType<typeof setTimeout>[] = [];

const stopTimers = (): void => {
  for (const timer of timers) clearTimeout(timer);
  timers = [];
};

/** Forgets the delivery, for a new match. */
export const clearDelivery = (): void => {
  stopTimers();
  deliveryStore.set(NO_DELIVERY);
};

/**
 * Starts the delivery of the energy of `roll`, at the moment its die lands.
 * It gives how long the die stays on the stage. With reduced motion no coin
 * flies, and the pips fill when the die lands.
 */
export const startDelivery = (roll: RollRecord): number => {
  stopTimers();
  const flies = !window.matchMedia(REDUCED_MOTION_QUERY).matches;
  // The plan draws from `Math.random`, never from the match source, as the carry does.
  const plan = planEnergyDelivery(roll.gain ?? 0, flies, Math.random);
  deliveryStore.set({ roll, flights: plan.flights, left: 0, landed: 0 });
  for (const flight of plan.flights) {
    timers.push(
      setTimeout(
        () => deliveryStore.set((current) => ({ ...current, left: current.left + 1 })),
        flight.leaveMs,
      ),
      setTimeout(
        () => deliveryStore.set((current) => ({ ...current, landed: current.landed + 1 })),
        flight.landMs,
      ),
    );
  }
  return plan.holdMs;
};

/** The energy of `roll` that no coin dropped yet. */
export const undelivered = (roll: RollRecord): number => {
  const delivery = deliveryStore.get();
  return delivery.roll === roll ? delivery.flights.length - delivery.landed : 0;
};
